// Submission review (Review artboard) plus status-page controls, decisions, messages and activity.
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react';
import {
  OPTIONS, SLOT_IDS, canTransition, firmHasSigned, optionLabel, slotDef,
  type DiscoveryNote, type IntakeFileView, type LinkResponse, type NoteSection, type MessageView, type Review, type DeleteOnboardingRequest, type DeleteOnboardingResponse, type SlotId, type TreatmentKind, type UpdateOnboardingRequest,
} from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { fmtCalendarDate, fmtDate, fmtMonthDay, fmtRelative, fmtShortDay, fmtTime, formatPhone, plural, zoneShort } from '../lib/format';
import { Banner, Button, Dialog, Pill, Spinner, TableBox, Td, TextArea, TextField, Th, cx } from '../ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMe } from './AdminShell';
import { SOURCE_FILE, agreementPill, statusTone, treatmentTone } from './labels';
import { LinkResult } from './LinkResult';
import { ActivityTab, DecisionsTab, MessagesTab, StatusControlsTab } from './ReviewTabs';
import { AgreementCard } from './AgreementCard';
import { AddNoteButton, CardNote, NoteBox, type OnNoteSaved } from './DiscoveryNotes';
import { GatesTab } from './GatesTab';
import { Documents } from './Documents';
import { answerSections } from '../intake/answerSections';

/** `onDeleted` runs after staff delete the (withdrawn) onboarding, with its firm name and what happened to its session. */
export function ReviewPanel({ id, onChanged, onDeleted }: {
  id: string; onChanged: () => Promise<void>; onDeleted?: (firmName: string, result: DeleteOnboardingResponse) => void;
}) {
  const [r, setR] = useState<Review | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { reportNeedsYou } = useMe();
  // Firm messages arrive flagged `unread` once (the load marks them read); keep the "New" marker for this visit.
  const unread = useRef(new Set<string>());
  // Note saves, numbered: a load that started before a save keeps that save, not the older server copy it read.
  const noteSaves = useRef({ seq: 0, last: new Map<NoteSection, { seq: number; note: DiscoveryNote | null }>() });
  const load = useCallback(async () => {
    const from = noteSaves.current.seq;
    try {
      const review = await api.get<Review>(`/api/admin/onboardings/${id}`);
      for (const m of review.messages) if (m.unread) unread.current.add(m.id);
      let notes = review.notes;
      for (const [section, x] of noteSaves.current.last) if (x.seq > from) notes = withNote(notes, section, x.note);
      setR({ ...review, notes, messages: review.messages.map((m) => ({ ...m, unread: unread.current.has(m.id) })) });
      setError(null);
      if (review.messages.some((m) => m.unread)) reportNeedsYou();
    }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not load this onboarding.'); }
  }, [id, reportNeedsYou]);
  useEffect(() => { void load(); }, [load]);

  const refresh = useCallback(async () => { await Promise.all([load(), onChanged()]); }, [load, onChanged]);
  const onNote = useCallback<OnNoteSaved>((section, note) => {
    const saves = noteSaves.current;
    saves.last.set(section, { seq: ++saves.seq, note });
    setR((prev) => prev && { ...prev, notes: withNote(prev.notes, section, note) });
  }, []);
  /** `done` is the success notice, or builds it from the saved review. */
  const patch = useCallback(async (body: UpdateOnboardingRequest, done?: string | ((saved: Review) => string)) => {
    setError(null);
    try {
      const saved = await api.patch<Review>(`/api/admin/onboardings/${id}`, body);
      if (done) setNotice(typeof done === 'function' ? done(saved) : done);
      await refresh();
    }
    catch (e) { setError(e instanceof ApiError ? e.message : 'That change did not save.'); }
  }, [id, refresh]);

  if (!r) return error ? <Banner tone="error" role="alert">{error}</Banner> : <Spinner label="Loading submission" />;

  return (
    <section aria-labelledby="sub-h" className="flex flex-col gap-4 rounded-2xl border border-line bg-white p-4 shadow-card sm:p-5">
      <ReviewHeader r={r} patch={patch} onMessage={(m) => setR({ ...r, messages: [...r.messages, m] })} setNotice={setNotice} refresh={refresh} onDeleted={onDeleted} />
      {notice && <Banner tone="success" role="status">{notice}</Banner>}
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      <SizingTiles r={r} />
      <Tabs r={r} patch={patch} reload={load} onNote={onNote} />
    </section>
  );
}

/** `notes` with one section set to `note`, or removed when it is null. */
function withNote(notes: Review['notes'], section: NoteSection, note: DiscoveryNote | null): Review['notes'] {
  const { [section]: _old, ...rest } = notes;
  return note ? { ...rest, [section]: note } : rest;
}

// ---------------------------------------------------------------- header

function ReviewHeader({ r, patch, onMessage, setNotice, refresh, onDeleted }: {
  r: Review; patch: (b: UpdateOnboardingRequest, done?: string | ((saved: Review) => string)) => Promise<void>;
  onMessage: (m: MessageView) => void; setNotice: (s: string) => void; refresh: () => Promise<void>;
  onDeleted?: (firmName: string, result: DeleteOnboardingResponse) => void;
}) {
  const h = r.header;
  const c = h.contact;
  const [asking, setAsking] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [confirm, setConfirm] = useState<null | 'handoff' | 'withdraw' | 'reissue' | 'delete'>(null);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', close);
    menuRef.current?.querySelector<HTMLButtonElement>('[role=menuitem]')?.focus();
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  const meta = h.submittedAt
    ? `Submitted ${fmtDate(h.submittedAt)}${h.minutesToComplete != null ? ` · ${plural(h.minutesToComplete, 'minute')} to complete` : ''}`
    : r.stageLabel;
  const contactLine = [c ? [c.name, c.role].filter(Boolean).join(', ') : null, c?.email, c?.phone ? formatPhone(c.phone) : null, h.statesText].filter(Boolean).join(' · ');

  // One button for the next staff step; everything else is in More actions. While the Discovery Session gate is open
  // (design §14) that is closing it, on the Gates tab; closing marks the session held, so it replaces that menu item.
  const discoveryOpen = r.gates[0]?.state === 'open' && h.submittedAt !== null && r.status !== 'withdrawn' && r.status !== 'handed_off';
  const draft = { label: 'Draft migration plan', run: () => void patch({ status: 'plan_drafted' }, 'Status set to Plan drafted.') };
  const next = discoveryOpen ? { label: 'Close Discovery Session', run: () => navigate({ search: location.search, hash: 'gates' }) }
    : canTransition(r.status, 'plan_drafted') ? draft
    : canTransition(r.status, 'plan_approved') ? { label: 'Mark plan approved', run: () => void patch({ status: 'plan_approved' }, 'Plan marked as approved.') }
    : canTransition(r.status, 'handed_off') ? { label: 'Create migration project', run: () => setConfirm('handoff') }
    : null;
  const items: Array<{ label: string; run: () => void; show: boolean; danger?: boolean }> = [
    { label: 'Preview as firm', run: () => navigate(`/admin/onboarding/${r.id}/preview`), show: true },
    { label: 'Reissue link', run: () => setConfirm('reissue'), show: r.status !== 'withdrawn' && r.status !== 'handed_off' },
    { label: 'Edit contact', run: () => setEditingContact(true), show: r.status !== 'withdrawn' && r.status !== 'handed_off' },
    { label: 'Close Discovery Session…', run: () => navigate({ search: location.search, hash: 'gates' }),
      show: r.gates[0]?.state === 'open' && r.status !== 'withdrawn' && r.status !== 'handed_off' },
    { ...draft, show: discoveryOpen && canTransition(r.status, 'plan_drafted') },
    { label: 'Withdraw', run: () => setConfirm('withdraw'), show: canTransition(r.status, 'withdrawn') },
    // Only once withdrawn, so deleting always takes two deliberate steps.
    { label: 'Delete onboarding…', run: () => setConfirm('delete'), show: r.status === 'withdrawn', danger: true },
  ];

  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted"><Pill tone={statusTone(r.status)} dot>{r.stageLabel}</Pill>{meta !== r.stageLabel && <span>{meta}</span>}</div>
        <h2 id="sub-h" className="mb-0.5 mt-1.5 text-xl font-bold tracking-[-0.01em]">{h.firmName}</h2>
        {contactLine && <div className="text-ink-2 [overflow-wrap:anywhere]">{contactLine}</div>}
        {h.inviteEmail.toLowerCase() !== c?.email?.toLowerCase() && <div className="text-[13px] text-muted [overflow-wrap:anywhere]">Emails go to {h.inviteName} · {h.inviteEmail}</div>}
        {!h.submittedAt && h.linkExpiresAt && <div className="text-[13px] text-muted">Link expires {fmtDate(h.linkExpiresAt)}</div>}
        <RecordFacts r={r} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={() => setAsking(true)}>Ask the firm a question</Button>
        {next && <Button variant="primary" size="sm" onClick={next.run}>{next.label}</Button>}
        <div ref={menuRef} className="relative" onKeyDown={(e) => {
          if (e.key === 'Escape') { setMenu(false); menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus(); }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const els = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]') ?? [])];
            const i = els.indexOf(document.activeElement as HTMLButtonElement);
            els[(i + (e.key === 'ArrowDown' ? 1 : -1) + els.length) % els.length]?.focus();
            e.preventDefault();
          }
        }}>
          <Button variant="secondary" aria-label="More actions" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)} size="sm" className="!w-9 !px-0"><MoreHorizontal aria-hidden="true" size={18} /></Button>
          {menu && (
            <div role="menu" className="absolute right-0 top-full z-30 mt-1.5 flex min-w-[220px] flex-col rounded-xl border border-line bg-white p-1.5 shadow-pop">
              {items.filter((i) => i.show).map((i) => (
                <button key={i.label} role="menuitem" type="button" className={cx('min-h-11 rounded-lg border-0 bg-white px-3 text-left text-sm hover:bg-panel focus:bg-panel', i.danger && 'font-semibold text-bad')} onClick={() => { setMenu(false); i.run(); }}>
                  {i.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <ContactDialog open={editingContact} r={r} onClose={() => setEditingContact(false)}
        onSaved={async (revoked) => {
          setEditingContact(false);
          setNotice(revoked ? 'Contact updated. Earlier links stopped working. Use Reissue link to send one to the new address.' : 'Contact updated.');
          await refresh();
        }} />
      <DeleteDialog open={confirm === 'delete'} r={r} onClose={() => setConfirm(null)} onDeleted={(res) => { setConfirm(null); onDeleted?.(h.firmName, res); }} />
      <AskDialog open={asking} id={r.id} onClose={() => setAsking(false)} onSent={(m) => { onMessage(m); setNotice('Message sent. It appears on the firm’s status page and was emailed to the contact.'); }} />
      <Dialog
        open={confirm === 'handoff'}
        title="Create migration project?"
        onClose={() => setConfirm(null)}
        footer={<><Button onClick={() => setConfirm(null)}>Cancel</Button><Button variant="primary" onClick={() => { setConfirm(null); void patch({ status: 'handed_off' }, 'Handed to the migration project.'); }}>Create migration project</Button></>}
      >
        <p className="m-0 text-ink-2">This marks {h.firmName} as handed off to a migration project. In v1 this changes the status only; the project itself is set up outside this app.</p>
      </Dialog>
      <WithdrawDialog open={confirm === 'withdraw'} r={r} onClose={() => setConfirm(null)} onWithdrawn={async (notice) => { setConfirm(null); setNotice(notice); await refresh(); }} />
      <ReissueDialog open={confirm === 'reissue'} r={r} onClose={() => setConfirm(null)} onDone={refresh} />
    </div>
  );
}

/** The firm list's row facts, on the record: Discovery Session, agreement and last activity. */
function RecordFacts({ r }: { r: Review }) {
  const k = r.kickoff;
  const session = k?.startsAt && (k.status === 'booked' || k.status === 'held')
    ? `${fmtShortDay(k.startsAt)} · ${fmtTime(k.startsAt)} ${zoneShort(new Date(k.startsAt))}${k.status === 'held' ? ' · held' : ''}`
    : k?.status === 'contact_requested' ? 'Firm asked for another time' : 'Not booked';
  const agreement = agreementPill(r.agreements[0]?.status ?? null, firmHasSigned(r.agreements, r.statusSteps));
  const last = r.events[0]?.at;
  return (
    <dl className="m-0 mt-2.5 flex flex-wrap gap-x-6 gap-y-1.5 text-[13px]">
      <div className="flex items-center gap-1.5"><dt className="text-muted">Discovery Session</dt><dd className="m-0 font-semibold">{session}</dd></div>
      <div className="flex items-center gap-1.5"><dt className="text-muted">Agreement</dt><dd className="m-0">{agreement ? <span title={agreement.title}><Pill tone={agreement.tone} className="!py-0.5">{agreement.label}</Pill></span> : <span className="font-semibold">Not sent</span>}</dd></div>
      {last && <div className="flex items-center gap-1.5"><dt className="text-muted">Last activity</dt><dd className="m-0 font-semibold">{fmtRelative(last)}</dd></div>}
    </dl>
  );
}

/** The invite contact: all firm mail goes here. A new email address stops the firm's current links. */
function ContactDialog({ open, r, onClose, onSaved }: { open: boolean; r: Review; onClose: () => void; onSaved: (linksRevoked: boolean) => Promise<void> }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Another open onboarding already has this email: staff can still save it on purpose.
  const [duplicate, setDuplicate] = useState(false);
  useEffect(() => { if (open) { setName(r.header.inviteName); setEmail(r.header.inviteEmail); setErr(null); setDuplicate(false); } }, [open, r.header.inviteName, r.header.inviteEmail]);
  const emailChanged = email.trim().toLowerCase() !== r.header.inviteEmail.toLowerCase();
  const valid = name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const booked = r.kickoff?.status === 'booked';
  const save = async (allowDuplicate = false) => {
    if (!valid) { setErr('Enter a name and a valid email.'); return; }
    setBusy(true);
    setErr(null);
    setDuplicate(false);
    try {
      const contact = { name: name.trim(), email: email.trim(), ...(allowDuplicate ? { allowDuplicate: true } : {}) };
      await api.patch(`/api/admin/onboardings/${r.id}`, { contact } satisfies UpdateOnboardingRequest);
      await onSaved(emailChanged);
    } catch (e) {
      setDuplicate(e instanceof ApiError && e.code === 'duplicate_onboarding');
      setErr(e instanceof ApiError ? e.message : 'The contact did not save.');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} title="Edit contact" onClose={busy ? () => undefined : onClose}
      footer={<><Button onClick={onClose} disabled={busy}>Cancel</Button><Button variant="primary" disabled={busy} onClick={() => void save()}>Save contact</Button></>}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!busy) void save(); }}>
        <p className="m-0 text-ink-2">Invites, booking confirmations and agreement emails for {r.header.firmName} go to this person.</p>
        <TextField label="Contact name" value={name} onChange={(e) => setName(e.target.value)} required />
        <TextField label="Contact email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setDuplicate(false); }} required />
        {emailChanged && (
          <Banner tone="warn">
            The firm’s current link stops working when you save. Reissue the link afterwards to send one to the new address.
            {booked && ' The Discovery Session invite moves to the new address too, and Outlook sends it there.'}
          </Banner>
        )}
        {duplicate ? (
          <Banner tone="warn" role="alert">
            <span className="block">{err} Save anyway only if the same person is the contact for both firms.</span>
            <Button variant="outline" size="sm" className="mt-2" disabled={busy} onClick={() => void save(true)}>Save anyway</Button>
          </Banner>
        ) : err && <Banner tone="error" role="alert">{err}</Banner>}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

/** Withdraw: confirm, then save. `onWithdrawn` gets the notice (says whether the session came off Outlook). Also opened from a pipeline row. */
export function WithdrawDialog({ open, r, onClose, onWithdrawn }: { open: boolean; r: Review; onClose: () => void; onWithdrawn: (notice: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) setErr(null); }, [open]);
  const withdraw = async () => {
    setBusy(true);
    setErr(null);
    try { await onWithdrawn(withdrawnNotice(r, await api.patch<Review>(`/api/admin/onboardings/${r.id}`, { status: 'withdrawn' } satisfies UpdateOnboardingRequest))); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That change did not save.'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog
      open={open}
      title={`Withdraw ${r.header.firmName}?`}
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" disabled={busy} onClick={() => void withdraw()}>Withdraw</Button></>}
    >
      <p className="m-0 text-ink-2">
        The firm’s link stops working and no more emails are sent. It moves to Archived.
        {sessionAhead(r.kickoff) && ` The Discovery Session on ${fmtDate(r.kickoff!.startsAt!)} is cancelled in Outlook, which tells the firm.`}
        {' '}This can’t be undone.
      </p>
      {err && <Banner tone="error" role="alert">{err}</Banner>}
    </Dialog>
  );
}

/** Reissue link: confirm (and choose whether to email it), then show the new link. Also opened from a pipeline row. */
export function ReissueDialog({ open, r, onClose, onDone }: { open: boolean; r: Review; onClose: () => void; onDone: () => Promise<void> }) {
  const h = r.header;
  const { me } = useMe();
  const [send, setSend] = useState(true);
  const [link, setLink] = useState<LinkResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setSend(true); setLink(null); setErr(null); } }, [open]);
  const reissue = async () => {
    setBusy(true);
    setErr(null);
    try { setLink(await api.post<LinkResponse>(`/api/admin/onboardings/${r.id}/links`, { send })); await onDone(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not reissue the link.'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog
      open={open}
      title={link ? 'New link created' : 'Reissue link?'}
      onClose={onClose}
      footer={link ? <Button variant="primary" onClick={onClose}>Done</Button>
        : <><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy} onClick={() => void reissue()}>Reissue link</Button></>}
    >
      {link ? <LinkResult result={link} email={send ? h.inviteEmail : null} systemMailbox={me?.systemMailbox} /> : (
        <>
          <p className="m-0 text-ink-2">Earlier links for {h.firmName} stop working immediately. Saved answers and files are kept.</p>
          <label className="flex items-start gap-2.5 text-sm">
            <input type="checkbox" className="accent-brand mt-0.5 h-[18px] w-[18px]" checked={send} onChange={(e) => setSend(e.target.checked)} />
            <span>Email the new link<span className="block text-[13px] text-muted">{me?.graph.connected ? 'Sends from your Outlook.' : `Sends from ${me?.systemMailbox ?? 'no-reply@vertekal.com'} with replies to you.`}</span></span>
          </label>
          {err && <Banner tone="error" role="alert">{err}</Banner>}
        </>
      )}
    </Dialog>
  );
}

/** A booked Discovery Session still ahead: withdraw and delete take it off Outlook. */
const sessionAhead = (k: Review['kickoff']) => !!k?.startsAt && k.status === 'booked' && new Date(k.startsAt).getTime() > Date.now();

/** Withdraw's notice, from the review before and after: says whether the session came off Outlook. */
const withdrawnNotice = (before: Review, saved: Review) => {
  if (!sessionAhead(before.kickoff)) return 'Onboarding withdrawn.';
  return sessionAhead(saved.kickoff)
    ? `Onboarding withdrawn, but the Discovery Session on ${fmtDate(saved.kickoff!.startsAt!)} couldn’t be removed from Outlook. Cancel the meeting there so the firm isn’t left with it.`
    : 'Onboarding withdrawn, and its Discovery Session was cancelled in Outlook.';
};

/** Permanently deletes a withdrawn onboarding once the firm name is typed. Kept when the agreement was signed. */
export function DeleteDialog({ open, r, onClose, onDeleted }: { open: boolean; r: Review; onClose: () => void; onDeleted: (res: DeleteOnboardingResponse) => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setTyped(''); setErr(null); } }, [open]);
  const name = r.header.firmName;
  const signed = firmHasSigned(r.agreements, r.statusSteps);
  const upcoming = sessionAhead(r.kickoff);
  const files = r.files.slots.reduce((n, s) => n + s.files.length, 0);
  const ready = typed.trim() === name.trim();
  const remove = async () => {
    setBusy(true);
    setErr(null);
    try { onDeleted(await api.del<DeleteOnboardingResponse>(`/api/admin/onboardings/${r.id}`, { confirmFirmName: typed } satisfies DeleteOnboardingRequest)); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not delete the onboarding.'); }
    finally { setBusy(false); }
  };
  if (signed) {
    return (
      <Dialog open={open} title={`${name} can’t be deleted`} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>OK</Button>}>
        <p className="m-0 text-ink-2">This firm signed the agreement, so its onboarding is kept for the record.</p>
      </Dialog>
    );
  }
  return (
    <Dialog open={open} title={`Delete ${name}?`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" disabled={busy || !ready} onClick={() => void remove()}>Delete permanently</Button></>}>
      <p className="m-0 text-ink-2">This permanently removes the onboarding from Platform. It can’t be undone.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-card bg-bad-bg p-3.5 text-[13px] text-ink-2">
          <div className="mb-1.5 text-sm font-semibold text-bad">Deleted</div>
          <ul className="m-0 flex flex-col gap-1 pl-[18px]">
            <li>Questionnaire answers and tools map</li>
            {Object.keys(r.notes).length > 0 && <li>Discovery Session notes</li>}
            {files > 0 && <li>{plural(files, 'uploaded file')}, removed from storage</li>}
            <li>Messages, decisions and activity</li>
            {upcoming ? <li>The upcoming Discovery Session, cancelled in Outlook (the firm gets the cancellation)</li>
              : r.kickoff && <li>Discovery Session record</li>}
          </ul>
        </div>
        <div className="rounded-card border border-line bg-panel p-3.5 text-[13px] text-ink-2">
          <div className="mb-1.5 text-sm font-semibold text-ink">Kept</div>
          <ul className="m-0 flex flex-col gap-1 pl-[18px]">
            <li>Emails already sent stay in Outlook and the send log</li>
            <li>A line under Settings, Deleted onboardings: who deleted it and when</li>
          </ul>
        </div>
      </div>
      <TextField label={`Type ${name} to confirm`} autoComplete="off" spellCheck={false} value={typed} onChange={(e) => setTyped(e.target.value)} />
      {err && <Banner tone="error" role="alert">{err}</Banner>}
    </Dialog>
  );
}

function AskDialog({ open, id, onClose, onSent }: { open: boolean; id: string; onClose: () => void; onSent: (m: MessageView) => void }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setBody(''); setErr(null); } }, [open]);
  const send = async () => {
    setBusy(true);
    try { onSent(await api.post<MessageView>(`/api/admin/onboardings/${id}/messages`, { body: body.trim() })); onClose(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not send the message.'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} title="Ask the firm a question" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !body.trim()} onClick={() => void send()}>Send</Button></>}>
      <TextArea label="Your question" hint="Appears on the firm’s status page and arrives by email." rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
      {err && <Banner tone="error" role="alert">{err}</Banner>}
    </Dialog>
  );
}

// ---------------------------------------------------------------- sizing

function Tile({ label, value, sub, small }: { label: string; value: string; sub: string; small?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-card border border-line bg-white px-3.5 py-2">
      <div className="text-xs font-semibold text-muted">{label}</div>
      <div className={cx('font-bold leading-tight tracking-[-0.01em]', small ? 'text-[15px]' : 'text-lg tabular-nums')}>{value}</div>
      <div className="truncate text-xs text-muted" title={sub}>{sub}</div>
    </div>
  );
}

function SizingTiles({ r }: { r: Review }) {
  const s = r.sizing;
  const b = s.clients.breakdown;
  const bits = b ? [
    b.individual != null && plural(b.individual, 'individual'),
    b.business != null && plural(b.business, 'business', 'businesses'),
    b.trust != null && plural(b.trust, 'trust'),
    b.nonprofit != null && plural(b.nonprofit, 'nonprofit'),
  ].filter(Boolean).join(' · ') : '';
  const templates = { in_software: 'Templates kept in software', in_documents: 'Templates kept in spreadsheets', none: 'No templates' } as const;
  const docs = s.documents;
  const none = docs.scope?.includes('none');
  const years = docs.years ? { current: 'Current', '2': '2 yrs', '3': '3 yrs', '5_plus': '5+ yrs' }[docs.years] : '—';
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2">
      <Tile label="Active clients" value={s.clients.total != null ? String(s.clients.total) : '—'} sub={bits || 'No breakdown'} />
      <Tile label="Open jobs" value={s.openJobs.count != null ? String(s.openJobs.count) : '—'} sub={s.openJobs.templates ? templates[s.openJobs.templates] : 'Templates not answered'} />
      <Tile label="Logins" value={s.logins.count != null ? String(s.logins.count) : '—'} sub={s.logins.firmSize === 'just_me' ? 'Solo firm' : s.logins.firmSize ? `${optionLabel('firmSize', s.logins.firmSize)} people` : 'Firm size not answered'} />
      <Tile label="Documents" value={none ? 'None' : years ?? '—'} sub={none ? 'Starting fresh' : docs.scope?.length ? `Final documents only${docs.size ? ` · ${OPTIONS.docsSize[docs.size]}` : ''}` : 'Not answered'} />
      <Tile label="Wants to go live" small value={optionLabel('targetTiming', s.timing) ?? (s.targetDate ? fmtCalendarDate(s.targetDate) : 'Not answered')}
        sub={[s.targetDate ? `Target date ${fmtCalendarDate(s.targetDate)}` : null, r.scope.freezeDate ? `Freeze date ${fmtDate(r.scope.freezeDate)}` : 'Freeze date set at kickoff'].filter(Boolean).join(' · ')} />
    </div>
  );
}

// ---------------------------------------------------------------- tools map

/** Rows with more tools or treatments than this start collapsed to a one-line summary. */
const TOOLS_SHOWN = 3;
/** Migration scope names this many systems, then "+N more". */
const SCOPE_SHOWN = 3;

function ToolsMap({ r, onChange, onNote }: { r: Review; onChange: () => Promise<void>; onNote: OnNoteSaved }) {
  const [saving, setSaving] = useState<string | null>(null);
  // Rows whose Add note was pressed; a row with a saved note always shows it.
  const [noting, setNoting] = useState<ReadonlySet<string>>(new Set());
  const note = (fn: string, on: boolean) => setNoting((s) => { const n = new Set(s); if (on) n.add(fn); else n.delete(fn); return n; });
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = async (fn: string, toolId: string | null, confirmed: boolean) => {
    const key = `${fn}:${toolId ?? 'row'}`;
    setSaving(key);
    setErr(null);
    try { await api.put(`/api/admin/onboardings/${r.id}/tools/${fn}/treatments/${encodeURIComponent(toolId ?? 'row')}`, { confirmed }); await onChange(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not save the confirmation.'); }
    finally { setSaving(null); }
  };
  const long = r.toolsMap.filter((row) => row.tools.length > TOOLS_SHOWN || row.treatments.length > TOOLS_SHOWN).map((row) => row.functionId);
  const allOpen = long.every((fn) => open.has(fn));
  const flip = (fn: string) => setOpen((s) => { const n = new Set(s); if (!n.delete(fn)) n.add(fn); return n; });
  return (
    <div className="flex min-w-0 flex-[999_1_600px] flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="card-title">Tools map and suggested treatment</h3>
        <span className="flex flex-wrap items-baseline gap-x-3 text-[13px] text-muted">
          Suggested by rules. Confirm each at the Discovery Session.
          {long.length > 0 && (
            <button type="button" className="border-0 bg-transparent p-0 font-semibold text-brand underline" onClick={() => setOpen(allOpen ? new Set() : new Set(long))}>
              {allOpen ? 'Collapse long rows' : 'Expand all'}
            </button>
          )}
        </span>
      </div>
      {err && <Banner tone="error" role="alert">{err}</Banner>}
      <TableBox minWidth={640} label="Tools map">
        <thead>
          <tr className="border-b border-line-soft">
            <Th className="w-[200px]">Business function</Th><Th>They use</Th><Th>Suggested treatment</Th><Th className="w-[130px]">Confirmed</Th>
          </tr>
        </thead>
        {r.toolsMap.map((row) => {
          const collapsible = long.includes(row.functionId);
          const expanded = !collapsible || open.has(row.functionId);
          const ts = row.treatments;
          const toolName = (id: string | null) => id === null ? row.label : row.tools.find((x) => x.id === id)?.label ?? id;
          // A row-level treatment covers every tool in the row; a per-tool one sits beside its tool.
          const used = (id: string | null, all: boolean) => row.unanswered ? 'Not answered' : row.notApplicable ? 'Not applicable'
            : id !== null ? toolName(id)
            : all || row.tools.length <= TOOLS_SHOWN ? row.tools.map((t) => t.label).join(', ')
            : `${row.tools.slice(0, TOOLS_SHOWN).map((t) => t.label).join(', ')} +${row.tools.length - TOOLS_SHOWN} more`;
          const saved = r.notes[row.functionId];
          const showNote = !!saved || noting.has(row.functionId);
          const pad = (i: number, n: number) => cx(i === 0 ? 'pt-3' : 'pt-1', i === n - 1 && !showNote ? 'pb-3' : 'pb-1');
          const fnCell = (
            <Td rowSpan={expanded ? Math.max(ts.length, 1) : 1} className={pad(0, 1)}>
              <div className="font-semibold">{row.label}</div>
              {row.usageNote && <div className="mt-0.5 text-[13px] text-muted [overflow-wrap:anywhere]">“{row.usageNote}”</div>}
              {collapsible && (
                <button type="button" aria-expanded={expanded} aria-controls={`tools-${row.functionId}`}
                  className="mt-1.5 inline-flex min-h-8 items-center gap-1 border-0 bg-transparent p-0 text-[13px] font-semibold text-brand"
                  onClick={() => flip(row.functionId)}>
                  {expanded ? <ChevronUp aria-hidden="true" size={16} /> : <ChevronDown aria-hidden="true" size={16} />}
                  {expanded ? 'Show less' : `Show all ${plural(Math.max(row.tools.length, ts.length), 'tool')}`}
                  <span className="sr-only"> for {row.label}</span>
                </button>
              )}
              {!showNote && <AddNoteButton label={row.label} onClick={() => note(row.functionId, true)} className="mt-1 flex" />}
            </Td>
          );
          return (
            <tbody key={row.functionId} id={`tools-${row.functionId}`} className={cx('border-b border-line-soft last:border-b-0', row.unanswered && 'bg-warn-tint')}>
              {expanded ? ts.map((t, i) => {
                const key = `${row.functionId}:${t.toolId ?? 'row'}`;
                return (
                  // The first line keeps one key in both states so the function cell (and the toggle's focus) survives a toggle.
                  // Both states render the lines as an array, so that key still matches with the note row beside them.
                  <tr key={i === 0 ? 'line-0' : key}>
                    {i === 0 && fnCell}
                    <Td className={cx(pad(i, ts.length), row.unanswered && 'font-semibold text-warn-fg')}>{used(t.toolId, true)}</Td>
                    <Td className={pad(i, ts.length)}>
                      <div className="flex min-h-7 flex-wrap items-center gap-x-1.5">
                        <Pill tone={treatmentTone(t.kind)}>{t.label}</Pill>
                        {t.detail && <span className="text-[13px] text-muted">{t.detail}</span>}
                      </div>
                    </Td>
                    <Td className={pad(i, ts.length)}>
                      <label className="flex min-h-7 items-center gap-2" title={t.confirmed ? `Confirmed by ${t.confirmed.by}, ${fmtDate(t.confirmed.at)}` : undefined}>
                        <input
                          type="checkbox"
                          className="h-[18px] w-[18px]"
                          aria-label={`Confirm ${t.label} for ${toolName(t.toolId)}`}
                          checked={!!t.confirmed}
                          disabled={saving === key}
                          onChange={(e) => void toggle(row.functionId, t.toolId, e.target.checked)}
                        />
                        {t.confirmed && <span className="text-[12px] text-muted">{t.confirmed.by.split(/[\s@]/)[0]}, {fmtMonthDay(t.confirmed.at)}</span>}
                      </label>
                    </Td>
                  </tr>
                );
              }) : [
                <tr key="line-0">
                  {fnCell}
                  <Td className={pad(0, 1)}>{used(null, false)}</Td>
                  <Td className={pad(0, 1)}><TreatmentSummary ts={ts} /></Td>
                  <Td className={cx(pad(0, 1), 'text-[13px]', ts.every((t) => t.confirmed) ? 'font-semibold text-ok-fg' : 'text-muted')}>
                    <div className="flex min-h-7 items-center whitespace-nowrap">{ts.filter((t) => t.confirmed).length} of {ts.length}</div>
                  </Td>
                </tr>,
              ]}
              {showNote && (
                <tr key="note">
                  <Td colSpan={4} className="!pb-3 !pt-1">
                    <NoteBox onboardingId={r.id} section={row.functionId} note={saved} label={row.label} onSaved={onNote}
                      autoFocus={!saved} onClose={() => note(row.functionId, false)} />
                  </Td>
                </tr>
              )}
            </tbody>
          );
        })}
      </TableBox>
    </div>
  );
}

/** Collapsed row: one pill per treatment with its count, and how many need a decision. */
function TreatmentSummary({ ts }: { ts: Review['toolsMap'][number]['treatments'] }) {
  const groups = new Map<string, { kind: TreatmentKind; n: number }>();
  for (const t of ts) groups.set(t.label, { kind: t.kind, n: (groups.get(t.label)?.n ?? 0) + 1 });
  const review = ts.filter((t) => t.needsReview).length;
  return (
    <div className="flex min-h-7 flex-wrap items-center gap-1.5">
      {[...groups].map(([label, g]) => <Pill key={label} tone={treatmentTone(g.kind)} className="whitespace-nowrap">{label}{g.n > 1 && <span className="font-normal">× {g.n}</span>}</Pill>)}
      {review > 0 && <span className="text-[13px] text-warn-fg">{review} need{review === 1 ? 's' : ''} review</span>}
    </div>
  );
}

// ---------------------------------------------------------------- right column

function Flags({ r, onNote }: { r: Review; onNote: OnNoteSaved }) {
  return (
    <div className="rounded-card border border-warn-line bg-warn-tint p-[18px]">
      <h3 className="mb-2 mt-0 text-[15px] font-semibold text-warn-fg">Flags for the Discovery Session</h3>
      {r.flags.length === 0 ? <div className="text-ink-2">No flags.</div> : (
        <ul className="m-0 flex flex-col gap-2 pl-[18px] text-sm text-ink-2">
          {r.flags.map((f, i) => <li key={`${f.code}-${i}`}><strong>{f.label}:</strong> {f.detail.replace(/\.?$/, '.')}</li>)}
        </ul>
      )}
      <CardNote onboardingId={r.id} section="flags" note={r.notes.flags} label="Flags" onSaved={onNote} />
    </div>
  );
}

/** Step 5 answers: what the firm wants covered in the Discovery Session. */
function FirmQuestions({ r, onNote }: { r: Review; onNote: OnNoteSaved }) {
  const a = r.answers;
  const interests = (a.interests ?? []).map((v) => optionLabel('interests', v) ?? v);
  const empty = !interests.length && !a.questionsText && !a.beforeMeetingText;
  return (
    <div className="rounded-card border border-line bg-white p-[18px]">
      <h3 className="mb-2 mt-0 text-[15px] font-semibold">Before the Discovery Session</h3>
      {empty ? <div className="text-sm text-muted">Nothing added yet.</div> : (
        <div className="flex flex-col gap-2.5 text-sm">
          {interests.length > 0 && (
            <div><div className="text-[13px] text-muted">Most interested in</div><div className="font-semibold">{interests.join(', ')}</div></div>
          )}
          {a.questionsText && (
            <div><div className="text-[13px] text-muted">Questions for us</div><div className="whitespace-pre-wrap [overflow-wrap:anywhere]">{a.questionsText}</div></div>
          )}
          {a.beforeMeetingText && (
            <div><div className="text-[13px] text-muted">Anything else before we meet</div><div className="whitespace-pre-wrap [overflow-wrap:anywhere]">{a.beforeMeetingText}</div></div>
          )}
        </div>
      )}
      <CardNote onboardingId={r.id} section="questions" note={r.notes.questions} label="Before the Discovery Session" onSaved={onNote} />
    </div>
  );
}

function Scope({ r, onNote }: { r: Review; onNote: OnNoteSaved }) {
  const s = r.scope;
  // Long lists name the first few; the full list is in the tooltip.
  const count = (xs: string[]) => (!xs.length ? '0'
    : `${xs.length} (${xs.slice(0, SCOPE_SHOWN).join(', ')}${xs.length > SCOPE_SHOWN ? ` +${xs.length - SCOPE_SHOWN} more` : ''})`);
  const full: Record<string, string> = { 'Source systems to migrate': s.sourceSystems.join(', '), 'Systems used for data': s.dataSystems.join(', ') };
  const rows: Array<[string, string]> = [
    ['Source systems to migrate', count(s.sourceSystems)],
    ['Systems used for data', count(s.dataSystems)],
    ['Records', `${s.records.clients ?? '—'} clients · ${s.records.openJobs ?? '—'} jobs`],
    ['Document scope', s.documents],
    ['Time history', s.timeHistory ? 'Needed' : 'Not needed'],
    ['Items to review', String(s.itemsToReview)],
  ];
  return (
    <div className="rounded-card border border-line bg-white p-[18px]">
      <h3 className="mb-2.5 mt-0 text-[15px] font-semibold">Migration scope</h3>
      <dl className="m-0 grid grid-cols-[minmax(120px,1fr)_minmax(0,1.3fr)] gap-x-3 gap-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <Fragment key={k}><dt className="text-muted">{k}</dt><dd className="m-0 text-right font-semibold" title={full[k] || undefined}>{v}</dd></Fragment>
        ))}
      </dl>
      <div className="mt-3 flex justify-between gap-3 border-t border-line-soft pt-3 text-sm">
        <span className="text-muted">Freeze date</span><span className="font-bold">{s.freezeDate ? fmtDate(s.freezeDate) : 'Set at kickoff'}</span>
      </div>
      <CardNote onboardingId={r.id} section="scope" note={r.notes.scope} label="Migration scope" onSaved={onNote} />
    </div>
  );
}

async function openDownload(file: IntakeFileView, setErr: (s: string | null) => void) {
  setErr(null);
  const win = window.open('', '_blank');
  try {
    const res = await api.get<{ url: string }>(`/api/admin/files/${file.id}/download`);
    if (win) { win.opener = null; win.location.href = res.url; } else window.location.href = res.url;
  } catch (e) {
    win?.close();
    setErr(e instanceof ApiError && e.code === 'scan_pending' ? 'This file is still being scanned.' : e instanceof ApiError && e.code === 'scan_rejected' ? 'This file failed the malware scan and can’t be downloaded.' : 'Could not download the file.');
  }
}

function Files({ r, onChange, patch }: { r: Review; onChange: () => Promise<void>; patch: (b: UpdateOnboardingRequest, done?: string | ((saved: Review) => string)) => Promise<void> }) {
  const [err, setErr] = useState<string | null>(null);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [emailing, setEmailing] = useState(false);
  const requested = r.files.requestedAt;
  const closed = r.status === 'withdrawn' || r.status === 'handed_off';
  // Uploads open only after the agreement is fully signed (in the app, or its status step ticked).
  const agreementSigned = r.statusSteps.some((s) => s.id === 'plan.agreement' && s.state === 'done');
  const missing = r.files.slots.filter((s) => s.countsAsMissing && s.status === 'needed').length;
  const emailMissing = async () => {
    setErr(null);
    setEmailNotice(null);
    setEmailing(true);
    try {
      await api.post(`/api/admin/onboardings/${r.id}/missing-files-email`, {});
      setEmailNotice('Missing-files email sent to the firm.');
    } catch (e) {
      // Only email_failed and the 4xx refusals mean nothing went out. A dropped connection or other server error
      // can come after the send, so don't invite a second one.
      const known = e instanceof ApiError && (e.code === 'email_failed' || (e.status >= 400 && e.status < 500));
      setErr(known ? (e as ApiError).message : 'We couldn’t confirm the email went out. Check the Activity tab before sending it again.');
    } finally {
      setEmailing(false);
    }
    // Refresh either way, so "Last emailed" and the activity log show what actually happened.
    await onChange();
  };
  const move = async (f: IntakeFileView, slotId: SlotId) => {
    setErr(null);
    try { await api.patch(`/api/admin/files/${f.id}`, { slotId }); await onChange(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not move the file.'); }
  };
  return (
    <div className="rounded-card border border-line bg-white p-[18px]">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="m-0 text-[15px] font-semibold">Migration files</h3>
        <span className="text-[13px] text-muted">{r.files.received} of {r.files.expected} received</span>
      </div>
      {/* Uploads stay closed until requested here, and can be requested once the agreement is fully signed. No email
          goes out: tell the firm yourself. Reopen later for the freeze-date exports. */}
      <div className={cx('mb-2.5 flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-[13px]', requested ? 'bg-ok-bg text-ok-fg' : 'bg-panel text-ink-2')}>
        <span data-testid="files-requested-state">
          {requested ? `Files requested ${fmtMonthDay(requested)}. Uploads are open on the firm’s status page.`
            : agreementSigned ? 'Uploads are closed. The firm can’t share files until you request them.'
            : 'Uploads are closed. You can request files once the agreement is fully signed.'}
        </span>
        {requested
          ? <Button variant="quiet" size="sm" onClick={() => void patch({ filesRequested: false }, 'File uploads closed on the firm’s status page.')}>Close uploads</Button>
          : <Button variant="outline" size="sm" disabled={closed || !agreementSigned} onClick={() => void patch({ filesRequested: true }, 'Files requested. Upload slots are now on the firm’s status page. No email was sent, so let the firm know.')}>Request files</Button>}
      </div>
      {/* The missing-files email is sent from here, never on a schedule. Home lists it when files are overdue. */}
      {requested && missing > 0 && !closed && (
        <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-warn-bg px-3 py-2 text-[13px] text-warn-fg">
          <span data-testid="missing-files-state">
            {missing} {missing === 1 ? 'file' : 'files'} not received yet.{r.files.missingEmailedAt ? ` Last emailed ${fmtMonthDay(r.files.missingEmailedAt)}.` : ''}
          </span>
          <Button variant="outline" size="sm" disabled={emailing} onClick={() => void emailMissing()}>Email the firm the missing files</Button>
        </div>
      )}
      {emailNotice && <Banner tone="success" role="status" className="mb-2">{emailNotice}</Banner>}
      {err && <Banner tone="error" role="alert" className="mb-2">{err}</Banner>}
      <ul className="m-0 flex list-none flex-col p-0 text-sm">
        {/* The firm's Documents uploads (the anything_else slot) are listed on the Documents card. */}
        {r.files.slots.filter((sl) => sl.id !== 'anything_else').map((sl) => {
          const clean = sl.files.filter((f) => f.scan === 'clean');
          return (
            <li key={sl.id} className="border-b border-line-soft py-2 last:border-b-0">
              <div className="flex justify-between gap-2">
                <span>{sl.title}</span>
                {sl.status === 'received'
                  ? <span className="flex-none font-semibold text-ok-fg">Received {clean[0] ? fmtMonthDay(clean[0].uploadedAt) : ''}</span>
                  : <span className={cx('flex-none font-semibold', sl.countsAsMissing ? 'text-warn-fg' : 'text-muted')}>{sl.countsAsMissing ? 'Needed' : 'Optional'}</span>}
              </div>
              {sl.files.length > 0 && (
                <ul className="m-0 mt-1 flex list-none flex-col gap-1.5 p-0 pl-3">
                  {sl.files.map((f) => (
                    <li key={f.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                      {f.scan === 'clean'
                        ? <button type="button" className="min-w-0 border-0 bg-transparent p-0 text-left font-semibold text-brand underline [overflow-wrap:anywhere]" onClick={() => void openDownload(f, setErr)}>{f.fileName}</button>
                        : <span className="min-w-0 text-muted [overflow-wrap:anywhere]" aria-disabled="true">{f.fileName}</span>}
                      <span className={cx(f.scan === 'rejected' ? 'font-semibold text-bad' : 'text-muted')}>
                        {f.scan === 'pending' ? 'Scanning' : f.scan === 'rejected' ? 'Rejected by scan' : fmtMonthDay(f.uploadedAt)}
                      </span>
                      <select
                        aria-label={`Move ${f.fileName} to another slot`}
                        className="h-8 max-w-[180px] rounded-lg border border-line-strong bg-white px-1 text-[13px]"
                        value={f.slotId}
                        onChange={(e) => void move(f, e.target.value as SlotId)}
                      >
                        {SLOT_IDS.map((s) => <option key={s} value={s}>{slotDef(s).title}</option>)}
                      </select>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mt-2 text-[13px] text-muted">{r.files.note}</div>
    </div>
  );
}

// ---------------------------------------------------------------- firm's answers

/** Every questionnaire answer, in the questionnaire's wording; tools rows read as on the tools map. */
function FirmAnswers({ r }: { r: Review }) {
  const toolText = (fn: string) => {
    const row = r.toolsMap.find((x) => x.functionId === fn);
    if (!row || row.unanswered) return '';
    const used = row.notApplicable ? 'Not applicable' : row.tools.map((t) => t.label).join(', ');
    return row.usageNote ? `${used} · “${row.usageNote}”` : used;
  };
  const c = r.answers.contact;
  const answers = c?.phone ? { ...r.answers, contact: { ...c, phone: formatPhone(c.phone) } } : r.answers;
  const sections = answerSections(answers, toolText, { staff: true });
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="card-title">Firm’s answers</h3>
        <span className="text-[13px] text-muted">
          {r.header.submittedAt ? `Everything the firm entered, as submitted ${fmtDate(r.header.submittedAt)}.` : 'Everything the firm has entered so far. Not submitted yet.'}
        </span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] items-start gap-3">
        {sections.map((sec) => (
          <section key={sec.step} aria-labelledby={`ans-${sec.step}`} className="rounded-card border border-line bg-white">
            <h4 id={`ans-${sec.step}`} className="m-0 border-b border-line-soft px-4 py-2.5 text-[15px] font-semibold">{sec.title}</h4>
            <dl className="m-0 px-4 py-1">
              {sec.rows.map((row) => (
                <div key={row.k} className="grid grid-cols-[minmax(110px,2fr)_3fr] gap-x-3 border-b border-line-soft py-2 text-sm last:border-b-0">
                  {/* The question quiet, the firm's answer strong, so a column of answers reads at a glance. */}
                  <dt className="text-[13px] text-muted">{row.k}</dt>
                  <dd className={cx('m-0 whitespace-pre-wrap [overflow-wrap:anywhere]', row.v ? 'font-semibold text-ink' : row.optional ? 'text-muted' : 'font-semibold text-warn-fg')}>
                    {row.v || (row.optional ? 'Nothing added' : 'Not answered')}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- checklist

function Checklist({ r }: { r: Review }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="card-title">Draft readiness checklist</h3>
        <span className="text-[13px] text-muted">Generated from the answers. Internal only, never shown to the firm.</span>
      </div>
      <TableBox minWidth={720} label="Draft readiness checklist">
        <thead>
          <tr className="border-b border-line-soft">
            <Th>ID</Th><Th>Item</Th><Th>Applies</Th><Th>Source</Th><Th>Treatment</Th><Th>Source file</Th>
          </tr>
        </thead>
        <tbody>
          {r.mrChecklist.map((m) => (
            <tr key={m.id} className="border-b border-line-soft last:border-b-0">
              <Td className="!py-[9px] text-muted">{m.id}</Td>
              <Td className="!py-[9px]">{m.item}</Td>
              <Td className="!py-[9px]">{m.applies === 'required' ? 'Required' : 'To confirm'}</Td>
              <Td className="!py-[9px]">{m.source}</Td>
              <Td className="!py-[9px]">{m.treatment}</Td>
              <Td className={cx('!py-[9px]', SOURCE_FILE[m.sourceFile]?.className)}>{SOURCE_FILE[m.sourceFile]?.label ?? m.sourceFile}</Td>
            </tr>
          ))}
        </tbody>
      </TableBox>
    </div>
  );
}

// ---------------------------------------------------------------- tabs

/** Every section of the review is one tab. `slug` is the URL hash, so a reload or a link (Home → #messages) opens it. */
const TABS = [
  { name: 'Discovery prep', slug: 'prep' }, { name: 'Gates', slug: 'gates' }, { name: 'Firm’s answers', slug: 'answers' }, { name: 'Files and agreement', slug: 'files' },
  { name: 'Readiness checklist', slug: 'checklist' }, { name: 'Status page', slug: 'status' }, { name: 'Decisions', slug: 'decisions' },
  { name: 'Messages', slug: 'messages' }, { name: 'Activity', slug: 'activity' },
] as const;
type TabName = (typeof TABS)[number]['name'];

function Tabs({ r, patch, reload, onNote }: {
  r: Review; patch: (b: UpdateOnboardingRequest, done?: string | ((saved: Review) => string)) => Promise<void>; reload: () => Promise<void>; onNote: OnNoteSaved;
}) {
  // The open tab is the URL hash (none or unknown: the first tab).
  const location = useLocation();
  const navigate = useNavigate();
  const i = Math.max(0, TABS.findIndex((t) => location.hash === `#${t.slug}`));
  const tab = TABS[i]!.name;
  const setTab = (n: number) => navigate({ search: location.search, hash: TABS[n]!.slug }, { replace: true });
  const box = useRef<HTMLDivElement>(null);
  // A link to a tab other than the first scrolls to it.
  useEffect(() => { if (i > 0) box.current?.scrollIntoView({ block: 'start' }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const newCount = r.messages.filter((m) => m.unread).length;
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const counts: Partial<Record<TabName, number>> = { Decisions: r.decisions.length, Messages: r.messages.length };
  return (
    <div ref={box} className="flex scroll-mt-6 flex-col gap-4">
      <div role="tablist" aria-label="Review sections" className="flex flex-wrap gap-1 border-b border-line-soft"
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length;
            setTab(n);
            refs.current[n]?.focus();
          }
        }}>
        {TABS.map(({ name: t }, n) => (
          <button
            key={t}
            ref={(el) => { refs.current[n] = el; }}
            role="tab"
            type="button"
            id={`tab-${n}`}
            aria-selected={tab === t}
            aria-controls={tab === t ? `panel-${n}` : undefined}
            tabIndex={tab === t ? 0 : -1}
            onClick={() => setTab(n)}
            className={cx('-mb-px min-h-11 border-0 border-b-2 bg-transparent px-3 text-sm', tab === t ? 'border-brand font-semibold text-ink' : 'border-transparent font-medium text-muted hover:text-ink')}
          >
            {t}{counts[t] ? ` (${counts[t]})` : ''}
            {t === 'Messages' && newCount > 0 && (
              <span className="ml-1.5 rounded-pill bg-warn-bg px-1.5 py-px text-xs font-bold text-warn-fg">{newCount} new</span>
            )}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`panel-${i}`} aria-labelledby={`tab-${i}`}>
        {tab === 'Discovery prep' && (
          <div className="flex flex-wrap items-start gap-6">
            <ToolsMap r={r} onChange={reload} onNote={onNote} />
            <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-3.5">
              <Flags r={r} onNote={onNote} />
              <FirmQuestions r={r} onNote={onNote} />
              <Scope r={r} onNote={onNote} />
            </div>
          </div>
        )}
        {tab === 'Gates' && <GatesTab r={r} reload={reload} />}
        {tab === 'Firm’s answers' && <FirmAnswers r={r} />}
        {tab === 'Files and agreement' && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))] items-start gap-3.5">
            <Documents r={r} reload={reload} />
            <Files r={r} onChange={reload} patch={patch} />
            <AgreementCard r={r} reload={reload} />
          </div>
        )}
        {tab === 'Readiness checklist' && <Checklist r={r} />}
        {tab === 'Status page' && <StatusControlsTab r={r} patch={patch} />}
        {tab === 'Decisions' && <DecisionsTab r={r} reload={reload} />}
        {tab === 'Messages' && <MessagesTab r={r} reload={reload} />}
        {tab === 'Activity' && <ActivityTab r={r} />}
      </div>
    </div>
  );
}
